import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Skeleton,
  Button,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Circle, Clock, Play, ArrowRight, History, RotateCcw, CalendarX2, CalendarClock } from 'lucide-react';
import dayjs from 'dayjs';

// LocalStorage key for stopped tasks (array of recently stopped tasks)
const STOPPED_TASKS_KEY = 'nct_stopped_tasks';

import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectActiveTimers, startTimerAsync } from '@/store/slices/timerSlice';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { Task, TaskStatus, TaskPriority } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import StatusBadge from '@/shared/components/StatusBadge';
import { isAdminOrManager } from '@/shared/utils/roles';

/**
 * TodaysTasksWidget
 * Shows tasks user has picked/committed to complete today
 * Only shows tasks you've explicitly added via "Add to Today" button
 * Zero-friction design - primary actions always visible
 */
const TodaysTasksWidget: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const activeTimers = useAppSelector(selectActiveTimers);

  const { data, isLoading, error } = useQuery({
    queryKey: ['todays-picks'],
    queryFn: () => taskService.getTodaysPicks(),
    staleTime: 30000,
  });

  const tasks = data ?? [];

  // Get stopped tasks from localStorage (array of tasks)
  const [stoppedTasks, setStoppedTasks] = React.useState<Array<{
    taskId: string;
    taskTitle: string;
    stoppedAt: string;
  }>>(() => {
    try {
      const stored = localStorage.getItem(STOPPED_TASKS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Listen for storage changes (cross-tab sync)
  React.useEffect(() => {
    const handleStorageChange = () => {
      try {
        const stored = localStorage.getItem(STOPPED_TASKS_KEY);
        setStoppedTasks(stored ? JSON.parse(stored) : []);
      } catch {
        setStoppedTasks([]);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleStartTimer = (taskId: string, taskTitle: string) => {
    if (user) {
      dispatch(startTimerAsync({ taskId, userId: user.id, taskTitle }));
      // Remove this task from stopped tasks list
      const updatedStoppedTasks = stoppedTasks.filter(t => t.taskId !== taskId);
      localStorage.setItem(STOPPED_TASKS_KEY, JSON.stringify(updatedStoppedTasks));
      setStoppedTasks(updatedStoppedTasks);
    }
  };

  /** Check if current user can use timer (ONLY Responsible users) */
  const canUseTimer = (task: Task): boolean => {
    const userId = user?.id;
    if (!userId) return false;
    return task.raci?.responsible?.includes(userId) || false;
  };

  // Track previous active timers to detect when any timer stops
  const previousTimerIds = React.useRef<string[]>(activeTimers.map(t => t.taskId));

  // Track when timers stop to update stopped tasks list
  React.useEffect(() => {
    const currentTimerIds = activeTimers.map(t => t.taskId);
    
    // Find tasks that were in previous but not in current (stopped tasks)
    const stoppedTaskIds = previousTimerIds.current.filter(
      id => !currentTimerIds.includes(id)
    );
    
    if (stoppedTaskIds.length > 0) {
      // Add newly stopped tasks to the list
      const newStoppedTasks = stoppedTaskIds
        .map(taskId => {
          const task = tasks.find(t => t.id === taskId);
          if (task) {
            return {
              taskId: task.id,
              taskTitle: task.title,
              stoppedAt: new Date().toISOString(),
            };
          }
          return null;
        })
        .filter(Boolean) as Array<{ taskId: string; taskTitle: string; stoppedAt: string }>;
      
      // Merge with existing stopped tasks (remove duplicates, keep latest)
      const updatedStoppedTasks = [
        ...newStoppedTasks,
        ...stoppedTasks.filter(st => !stoppedTaskIds.includes(st.taskId)),
      ];
      
      localStorage.setItem(STOPPED_TASKS_KEY, JSON.stringify(updatedStoppedTasks));
      setStoppedTasks(updatedStoppedTasks);
    }
    
    previousTimerIds.current = currentTimerIds;
  }, [activeTimers, tasks, stoppedTasks]);

  const handleViewAll = () => {
    navigate('/tasks');
  };

  // Sort tasks: In progress first, then rescheduled tasks at end, then by priority
  const sortedTasks = [...tasks].sort((a, b) => {
    const todayStr = dayjs().format('YYYY-MM-DD');
    const aIsRescheduled = a.rescheduledFromDates?.includes(todayStr) || false;
    const bIsRescheduled = b.rescheduledFromDates?.includes(todayStr) || false;
    
    // Rescheduled tasks go to the end
    if (aIsRescheduled && !bIsRescheduled) return 1;
    if (!aIsRescheduled && bIsRescheduled) return -1;
    
    // In progress tasks first (among non-rescheduled)
    if (a.status === TaskStatus.IN_PROGRESS && b.status !== TaskStatus.IN_PROGRESS) return -1;
    if (b.status === TaskStatus.IN_PROGRESS && a.status !== TaskStatus.IN_PROGRESS) return 1;
    
    // Then by priority
    const priorityOrder = { p1: 0, p2: 1, p3: 2, p4: 3 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  // Count rescheduled vs active tasks
  const todayStr = dayjs().format('YYYY-MM-DD');
  const activeTasks = tasks.filter(t => !t.rescheduledFromDates?.includes(todayStr));
  const rescheduledTasks = tasks.filter(t => t.rescheduledFromDates?.includes(todayStr));

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Today's Tasks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeTasks.length} picked{rescheduledTasks.length > 0 ? `, ${rescheduledTasks.length} rescheduled` : ''} task{tasks.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Button
            endIcon={<ArrowRight size={16} />}
            onClick={handleViewAll}
            sx={{ textTransform: 'none' }}
          >
            View All
          </Button>
        </Box>

        {/* Tasks List */}
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={72} />
            ))}
          </Box>
        ) : tasks.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              bgcolor: 'background.default',
              borderRadius: 2,
            }}
          >
            <CalendarClock size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
            <Typography variant="h6" gutterBottom>
              No tasks picked for today
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pick tasks from Live Tasks below to start your day
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {sortedTasks.slice(0, 5).map((task, index) => {
              const stoppedTask = stoppedTasks.find(st => st.taskId === task.id);
              const isRescheduled = task.rescheduledFromDates?.includes(todayStr) || false;
              return (
                <TaskListItem
                  key={task.id}
                  task={task}
                  isTimerActive={activeTimers.some(t => t.taskId === task.id)}
                  isLastRunning={!!stoppedTask}
                  lastRunningSince={stoppedTask?.stoppedAt}
                  onStartTimer={() => handleStartTimer(task.id, task.title)}
                  isLast={index === Math.min(sortedTasks.length, 5) - 1}
                  isRescheduled={isRescheduled}
                  canTimer={canUseTimer(task)}
                />
              );
            })}
          </List>
        )}

        {/* Show more indicator */}
        {tasks.length > 5 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button size="small" onClick={handleViewAll}>
              +{tasks.length - 5} more tasks
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Individual task item component
interface TaskListItemProps {
  task: Task;
  isTimerActive: boolean;
  isLastRunning?: boolean;
  lastRunningSince?: string;
  onStartTimer: () => void;
  isLast: boolean;
  isRescheduled?: boolean;
  canTimer: boolean;
}

const TaskListItem: React.FC<TaskListItemProps> = ({
  task,
  isTimerActive,
  isLastRunning = false,
  lastRunningSince,
  onStartTimer,
  isLast,
  isRescheduled = false,
  canTimer,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/tasks?taskId=${task.id}`);
  };

  // Calculate deadline status
  const getDeadlineInfo = () => {
    const now = dayjs();
    const deadline = dayjs(task.dueDate);
    const diffHours = deadline.diff(now, 'hour');

    // If task is rescheduled from today, show rescheduled status
    if (isRescheduled) {
      return { label: `Rescheduled to ${deadline.format('MMM D')}`, color: 'warning' as const, isRescheduled: true };
    }
    
    if (task.status === TaskStatus.COMPLETED) {
      return { label: 'Completed', color: 'success' as const, isRescheduled: false };
    }
    if (diffHours < 0) {
      return { label: 'Overdue', color: 'error' as const, isRescheduled: false };
    }
    if (diffHours < 4) {
      return { label: `${diffHours}h left`, color: 'warning' as const, isRescheduled: false };
    }
    return { label: deadline.format('h:mm A'), color: 'default' as const, isRescheduled: false };
  };

  const deadlineInfo = getDeadlineInfo();

  // Calculate time since last run
  const getTimeSinceLastRun = () => {
    if (!lastRunningSince) return '';
    const stoppedAt = dayjs(lastRunningSince);
    const now = dayjs();
    const diffMinutes = now.diff(stoppedAt, 'minute');
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  return (
    <ListItem
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
        borderRadius: isLast ? '0 0 8px 8px' : 0,
        cursor: 'pointer',
        bgcolor: isRescheduled ? 'warning.50' : isLastRunning ? 'action.selected' : 'transparent',
        borderLeft: isRescheduled ? '3px solid' : isLastRunning ? '3px solid' : 'none',
        borderLeftColor: isRescheduled ? 'warning.main' : isLastRunning ? 'info.main' : 'transparent',
        opacity: isRescheduled ? 0.85 : 1,
        '&:hover': {
          bgcolor: isRescheduled ? 'warning.100' : isLastRunning ? 'action.selected' : 'action.hover',
        },
      }}
      onClick={handleClick}
      secondaryAction={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isLastRunning && !isRescheduled && (
            <Chip
              size="small"
              icon={<History size={12} />}
              label={getTimeSinceLastRun()}
              color="info"
              variant="outlined"
              sx={{ height: 24, fontSize: '0.7rem' }}
            />
          )}
          {isRescheduled ? (
            <Tooltip title="This task was due today but rescheduled. It counts as a miss for today.">
              <Chip
                size="small"
                icon={<CalendarClock size={12} />}
                label={deadlineInfo.label}
                color="warning"
                sx={{ height: 24, fontSize: '0.65rem' }}
              />
            </Tooltip>
          ) : (
            <Chip
              size="small"
              icon={<Clock size={12} />}
              label={deadlineInfo.label}
              color={deadlineInfo.color}
              sx={{ height: 24 }}
            />
          )}
          {task.status !== TaskStatus.COMPLETED && !isRescheduled && canTimer && (
            <Tooltip title={isLastRunning ? 'Resume last task' : 'Start timer'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onStartTimer();
                }}
                disabled={isTimerActive}
                sx={{
                  bgcolor: isTimerActive ? 'success.100' : isLastRunning ? 'info.50' : 'primary.50',
                  '&:hover': {
                    bgcolor: isTimerActive ? 'success.200' : isLastRunning ? 'info.100' : 'primary.100',
                  },
                }}
              >
                {isLastRunning ? (
                  <RotateCcw
                    size={14}
                    style={{ color: '#0ea5e9' }}
                  />
                ) : (
                  <Play
                    size={14}
                    fill={isTimerActive ? '#16a34a' : '#2563eb'}
                    style={{ color: isTimerActive ? '#16a34a' : '#2563eb' }}
                  />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 2 }}>
        {isRescheduled ? (
          <Tooltip title="Missed - Rescheduled">
            <CalendarX2 size={18} style={{ color: '#f59e0b' }} />
          </Tooltip>
        ) : task.status === TaskStatus.COMPLETED ? (
          <CheckCircle size={18} style={{ color: '#22c55e' }} />
        ) : task.status === TaskStatus.IN_PROGRESS ? (
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'white' }} />
          </Box>
        ) : (
          <Circle size={18} style={{ color: '#d4d4d4' }} />
        )}
      </Box>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                textDecoration: task.status === TaskStatus.COMPLETED || isRescheduled ? 'line-through' : 'none',
                color: task.status === TaskStatus.COMPLETED || isRescheduled ? 'text.secondary' : 'text.primary',
              }}
            >
              {task.title}
            </Typography>
            <PriorityBadge priority={task.priority} size="small" />
            {isRescheduled && (
              <Chip
                label="MISS"
                size="small"
                color="error"
                variant="outlined"
                sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, px: 0.5 }}
              />
            )}
          </Box>
        }
        secondary={
          isRescheduled ? (
            <Typography variant="caption" color="warning.main">
              Was due today • Now due {dayjs(task.dueDate).format('MMM D')}
            </Typography>
          ) : (task.projectName || task.projectId) ? (
            <Typography variant="caption" color="text.secondary">
              Project: {task.projectName || task.projectId}
            </Typography>
          ) : null
        }
      />
    </ListItem>
  );
};

export default TodaysTasksWidget;
